import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getDoctorsByFacility,
  getResidents,
  getRestrictedDates,
  saveTransportRequest,
  updateResidentDetails,
} from '../../../../api/transports';
import useFetchList from '../../../../hooks/useFetchList';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import Iconify from '../../../../reusable-components/iconify';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import ProgressBar from '../../../../reusable-components/progress-bar/ProgressBar';
import AdditionalDetailsStep from './AdditionalDetailsStep';
import { formReducer, initialFormState } from './formReducer';
import InitialTripInfoStep from './InitialTripInfoStep';
import TripInfoDetailsStep from './TripInfoDetailsStep';
import { buildTransportRequestPayload, labelSort } from './utils';
import { FIELDS, TRANSPORT_TIME_ZONE_ID } from '../constants';

NewRequestForm.propTypes = {
  setOpen: PropTypes.func,
  requestDataLoaded: PropTypes.bool,
  facilities: PropTypes.array,
  onDialogSizeChange: PropTypes.func,
};

export default function NewRequestForm({ setOpen, facilities, requestDataLoaded = true, onDialogSizeChange }) {
  const [confirmedInitialTripInfo, setConfirmedInitialTripInfo] = useState(false);
  const [isInitialTripInfoSelected, setIsInitialTripInfoSelected] = useState(false);
  const [confirmedTripInfoDetails, setConfirmedTripInfoDetails] = useState(false);
  const [isTripInfoDetailsSelected, setIsTripInfoDetailsSelected] = useState(false);
  const [isAdditionalDetailsSelected, setIsAdditionalDetailsSelected] = useState(false);
  const [pendingResidentDetailsUpdate, setPendingResidentDetailsUpdate] = useState(null);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [formState, formDispatch] = useReducer(formReducer, initialFormState);
  const setFields = (fields) => formDispatch({ type: 'setFields', fields });
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
  };

  const handleBack = () => {
    if (confirmedInitialTripInfo && confirmedTripInfoDetails) {
      setConfirmedTripInfoDetails(false);
    } else if (confirmedInitialTripInfo && !confirmedTripInfoDetails) {
      setConfirmedInitialTripInfo(false);
    }
  };

  // Send dialog size up based on current step to ensure proper sizing for content
  React.useEffect(() => {
    if (onDialogSizeChange) {
      if (confirmedInitialTripInfo && !confirmedTripInfoDetails) {
        onDialogSizeChange('lg');
      } else if (confirmedTripInfoDetails) {
        onDialogSizeChange('md');
      } else {
        onDialogSizeChange('sm');
      }
    }
  }, [confirmedInitialTripInfo, confirmedTripInfoDetails, onDialogSizeChange]);

  const [residentsList, residentsLoading] = useFetchList(
    getResidents,
    [formState.FacilityId],
    formState.FacilityId ? [formState.FacilityId] : [null],
    labelSort
  );

  const [doctorsRefetchTrigger, setDoctorsRefetchTrigger] = React.useState(0);
  const [doctorsList, doctorsLoading] = useFetchList(
    getDoctorsByFacility,
    [formState.FacilityId, doctorsRefetchTrigger],
    formState.FacilityId ? [formState.FacilityId] : [null],
    labelSort
  );

  // Restricted Dates
  const [restrictedDates, restrictedDatesLoading] = useFetchList(
    getRestrictedDates,
    [formState.FacilityId],
    formState.FacilityId ? [formState.FacilityId] : [null]
  );

  const handleSubmit = async () => {
    if (formState.IsRecurring && !formState.AppointmentDateTime) {
      setSubmitError('Recurring transports require an appointment date and time');
      return;
    }
    const payload = buildTransportRequestPayload(formState, FIELDS);
    if (payload.IsRecurring) {
      payload[FIELDS.TIME_ZONE_ID] = TRANSPORT_TIME_ZONE_ID;
    }
    setSubmitLoading(true);
    setSubmitError('');
    try {
      if (pendingResidentDetailsUpdate) {
        await updateResidentDetails(pendingResidentDetailsUpdate);
      }

      const response = await saveTransportRequest(payload);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to submit request');
      }
      const { data } = response;
      const requestId = data?.id;
      if (requestId) {
        setOpen?.(false);
        navigate(`/dashboard/transports/request-details/${requestId}`, { state: { request: data } });
      }
    } catch (err) {
      setSubmitError(err.message || 'Error submitting request');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {confirmedInitialTripInfo && (
          <Button title="Back" onClick={handleBack} sx={{ color: 'gray', maxWidth: 20, left: 8, position: 'absolute' }}>
            <Iconify icon="ic:baseline-arrow-back" width={20} height={20} />
          </Button>
        )}
        {confirmedInitialTripInfo && confirmedTripInfoDetails ? 'Additional Details' : 'Trip Information'}
        <Box sx={{ m: 2 }}>
          <ProgressBar values={[100, confirmedInitialTripInfo ? 100 : 0, confirmedTripInfoDetails ? 100 : 0]} />
        </Box>
        {setOpen && <CloseIconButton onClick={handleClose} />}
      </DialogTitle>

      <DialogContent>
        {!requestDataLoaded && <LoadingAnimation />}
        {!confirmedInitialTripInfo && requestDataLoaded && (
          <InitialTripInfoStep
            facilities={facilities}
            form={{ formState, formDispatch }}
            setIsInitialTripInfoSelected={setIsInitialTripInfoSelected}
            setPendingResidentDetailsUpdate={setPendingResidentDetailsUpdate}
            setFields={setFields}
            residentsList={residentsList}
            residentsLoading={residentsLoading}
          />
        )}
        {confirmedInitialTripInfo && !confirmedTripInfoDetails && (
          <TripInfoDetailsStep
            form={{ formState, formDispatch }}
            setFields={setFields}
            setIsTripInfoDetailsSelected={setIsTripInfoDetailsSelected}
            restrictedDates={restrictedDates}
            restrictedDatesLoading={restrictedDatesLoading}
            doctorsList={doctorsList}
            doctorsLoading={doctorsLoading}
            setDoctorsRefetchTrigger={setDoctorsRefetchTrigger}
          />
        )}
        {confirmedInitialTripInfo && confirmedTripInfoDetails && (
          <AdditionalDetailsStep
            form={{ formState, formDispatch }}
            setFields={setFields}
            setIsAdditionalDetailsSelected={setIsAdditionalDetailsSelected}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center', zIndex: 1, pb: 2, px: 2 }}>
        {!confirmedInitialTripInfo && (
          <Button
            variant="contained"
            disabled={!isInitialTripInfoSelected}
            onClick={() => setConfirmedInitialTripInfo(true)}
            sx={{ width: '50%' }}
          >
            Next
          </Button>
        )}
        {confirmedInitialTripInfo && !confirmedTripInfoDetails && (
          <Button
            variant="contained"
            disabled={!isTripInfoDetailsSelected}
            onClick={() => setConfirmedTripInfoDetails(true)}
            sx={{ width: '50%' }}
          >
            Next
          </Button>
        )}
        {confirmedInitialTripInfo && confirmedTripInfoDetails && (
          <Box sx={{ width: '50%' }}>
            {submitError && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="center">
                  <Typography color="error" variant="body2">
                    {submitError}
                  </Typography>
                </Box>
              </Grid>
            )}
            <AwaitButton
              variant="contained"
              onClick={handleSubmit}
              sx={{ width: '100%' }}
              startIcon={<Iconify icon="fa:paper-plane-o" />}
              disabled={!isAdditionalDetailsSelected}
              loading={submitLoading}
            >
              Submit Request
            </AwaitButton>
          </Box>
          // </Tooltip>
        )}
      </DialogActions>
    </>
  );
}
